import { Quote as AcrossQuote } from '@across-protocol/app-sdk';
import { TokenInResponse } from '@frontend/client/hooks/tokenIn';
import { Quote } from '@frontend/common';
import {
  YodlPaymentPriceFeed,
  YodlPaymentPriceFeedTuple,
} from '@frontend/shared/pricefeeds';
import { SavedLink, TokenValue } from '@frontend/types';
import { TokenInfo } from '@yodlpay/tokenlists';
import { Address, WalletCapabilities } from 'viem';
import { create } from 'zustand';
import { PaymentPreferences } from '../hooks/preferences';

export const validThemeAccents = [
  'gray',
  'gold',
  'bronze',
  'brown',
  'yellow',
  'amber',
  'orange',
  'tomato',
  'red',
  'ruby',
  'crimson',
  'pink',
  'plum',
  'purple',
  'violet',
  'iris',
  'indigo',
  'blue',
  'cyan',
  'teal',
  'jade',
  'green',
  'grass',
  'lime',
  'mint',
  'sky',
];
export const validThemeGrays = [
  'auto',
  'gray',
  'mauve',
  'slate',
  'sage',
  'olive',
  'sand',
];
export type YodlTheme = {
  accent:
    | 'gray'
    | 'gold'
    | 'bronze'
    | 'brown'
    | 'yellow'
    | 'amber'
    | 'orange'
    | 'tomato'
    | 'red'
    | 'ruby'
    | 'crimson'
    | 'pink'
    | 'plum'
    | 'purple'
    | 'violet'
    | 'iris'
    | 'indigo'
    | 'blue'
    | 'cyan'
    | 'teal'
    | 'jade'
    | 'green'
    | 'grass'
    | 'lime'
    | 'mint'
    | 'sky';
  gray: 'auto' | 'gray' | 'mauve' | 'slate' | 'sage' | 'olive' | 'sand';
};

/**
 * External payment are linked to an external payment provider.
 * 1. Resolve of a QR Code
 * 2. Ensure external payment provider approves
 *
 */
export type ExternalPayment = {
  env: string;
  ensPrimaryName: string;
  memo: string;
  merchantName: string;
  status: string;
  originalAmount:
    | undefined
    | null
    | {
        amount: number;
        decimals: number;
        rate: number;
        currency: string;
      };
};

export type TokenInVariant = 'transfer' | 'stableswap' | 'tokenswap' | 'bridge';

export type TokenSelection = {
  token: TokenInfo;
  variant: TokenInVariant;
  tokenOuts?: TokenInfo[];
  convenienceFee?: bigint;
};

export const EMPTY_TOKEN_IN = {
  amount: {
    value: 0n,
    decimals: 1,
  },
  error: null,
  isLoading: true,
} as TokenInResponse;

const initialState = {
  handle: undefined,
  yd: undefined, // yodl id to track meta data
  walletCapabilities: undefined,
  theme: {
    accent: 'indigo',
    gray: 'auto',
  } as YodlTheme,
  // URL params
  playwright: undefined,
  // Receiver
  receiver: undefined,
  receiverHandle: undefined,
  receiverBalances: [],

  preferencesFromYodl: undefined,
  preferencesFromENS: undefined,

  // Commerce State
  redirectUrl: undefined,
  autoRedirect: false,

  hideUserPopups: true,

  // Wallet Connection
  sender: undefined,
  senderInfo: undefined,
  senderBalances: [],
  // TokenOut Dialog
  invoiceAmount: undefined, // what the user enters
  invoiceCurrency: 'USD', // what currency the user selects

  pricefeeds: undefined, // pricefeed (recently looked up)
  exchangeRate: undefined,
  pricefeed0: undefined, // pricefeed (recently looked up)

  tokenOut: undefined,
  tokenOutAmount: undefined, // the tokenOutAmount calculated from invoiceAmount x priceFeeds
  // TokenIn Dialog
  tokenIn: undefined,
  tokenSelection: undefined,
  tokenInAmount: EMPTY_TOKEN_IN,

  // User Selection:
  selectedPaymentPreference: undefined,
  paymentNotification: undefined,

  swapQuote: undefined,
  swapQuotes: [],
  // Other
  savedLinks: [],
  urlConfig: {} as PaymentConfig,
  recentTransactionHash: undefined, // Used to send the transaction hash to the receiver to generate a link

  acrossQuote: undefined,
  paymentMemo: undefined,
  communityEnsName: undefined,

  externalPayment: undefined,
  pmtMetaCommunity: undefined,
  pmtMetaYapp: undefined,
};

export type PaymentConfig = {
  amount?: number;
  currency?: string;
  chainIds?: number[];
  tokenSymbols?: string[];
  memo?: string;
  theme?: string;
  urlTokenIn?: TokenInfo | undefined;
  redirectUrl?: string;
  autoRedirect?: boolean;
  webhooks?: string[];
  og?: {
    baseUrl?: string;
  };
};

export type CommunityInfo = {
  address?: Address;
  ensName?: string;
  userEnsName?: string;
  communityFeeBps?: bigint;
};

type YodlPaymentStoreType = {
  yd: string | undefined; // bigint
  playwright: Address | undefined;
  walletCapabilities: WalletCapabilities | undefined;
  recentTransactionHash?: string;
  theme: YodlTheme;
  handle: string | undefined;

  receiver: Address | undefined;
  receiverHandle?: string;

  sender: Address | undefined;
  senderInfo: any;
  // useful to remember "previous" token when switching chains

  preferencesFromYodl: PaymentConfig | undefined;
  preferencesFromENS: PaymentConfig | undefined;

  // invoiceAmount is entered manually by the user. unlikely to be bigint-material
  invoiceAmount: number | undefined;
  invoiceCurrency: string;
  pricefeeds: YodlPaymentPriceFeedTuple | undefined;

  exchangeRate: bigint | undefined;
  // pricefeed0 converts the users selected invoiceCurrency into USD
  pricefeed0: YodlPaymentPriceFeed | undefined;

  tokenInAmount: TokenInResponse;
  tokenIn: TokenSelection | undefined;
  tokenSelection: TokenSelection | undefined;
  tokenOut: TokenInfo | undefined;
  tokenOutAmount: TokenValue | undefined;

  savedLinks: SavedLink[];

  swapQuote: Quote | undefined;

  // payment meta:
  pmtMetaCommunity: Address | undefined;
  setPmtMetaCommunity: (pmtMetaCommunity: Address | undefined) => void;
  pmtMetaYapp: [Address, string] | undefined;
  setPmtMetaYapp: (pmtMetaYapp: [Address, string] | undefined) => void;

  urlConfig: PaymentConfig;

  selectedPaymentPreference: PaymentPreferences | undefined;

  externalPayment: ExternalPayment | undefined;
  setExternalPayment: (externalPayment: ExternalPayment | undefined) => void;

  // Commerce State
  redirectUrl: string | undefined;
  setRedirectUrl: (redirectUrl: string | undefined) => void;
  autoRedirect: boolean;
  setAutoRedirect: (autoRedirect: boolean) => void;
  // Used to hide popup in some pages
  hideUserPopups: boolean;
  setHideUserPopups: (hideUserPopups: boolean) => void;

  // This is used to bridge tokens
  acrossQuote: AcrossQuote | undefined;
  setAcrossQuote: (acrossQuote: AcrossQuote | undefined) => void;

  paymentNotification: any;

  setYd: (yd: string) => void;
  setWalletCapabilities: (
    walletCapabilities: WalletCapabilities | undefined,
  ) => void;
  setTheme: (theme: YodlTheme) => void;
  setHandle: (handle: string | undefined) => void;
  setPlaywright: (playwright?: Address) => void;
  setUrlConfig: (urlConfig: PaymentConfig) => void;
  setReceiver: (address?: Address) => void;
  setSender: (address: Address) => void;
  setTokenOut: (tokenOut: TokenInfo | undefined) => void;
  setTokenIn: (tokenIn: TokenSelection | undefined) => void;
  setTokenSelection: (tokenSelection: TokenSelection | undefined) => void;
  setInvoiceAmount: (invoiceAmount: number | undefined) => void;

  setInvoiceCurrency: (currency: string) => void;
  setPricefeeds: (pricefeeds: YodlPaymentPriceFeedTuple | undefined) => void;
  setExchangeRate: (exchangeRate: bigint | undefined) => void;

  setTokenInAmount: (tokenInAmount: TokenInResponse) => void;
  setTokenOutAmount: (tokenOutAmount: TokenValue | undefined) => void;

  setSwapQuote: (swapQuote: Quote | undefined) => void;

  setSavedLinks: (savedlinks: SavedLink[]) => void;

  setRecentTransactionHash: (hash: string | undefined) => void;

  setPreferencesFromYodl: (preferences?: PaymentConfig) => void;
  setPreferencesFromENS: (preferences: PaymentConfig) => void;

  setSelectedPaymentPreference: (preferences: PaymentPreferences) => void;
  setPaymentNotification: (notification: any) => void;

  paymentMemo?: string;
  setPaymentMemo: (memo?: string) => void;

  communityInfo?: CommunityInfo;
  setCommunityInfo: (communityInfo?: CommunityInfo) => void;

  reset: () => void;
};

// priceFeeds <- [invoiceCurrency, tokenOut]
// tokenOutAmount <- [invoiceAmount, priceFeeds, tokenOut]

export const useYodlStore = create<YodlPaymentStoreType>((set, get) => ({
  ...initialState,

  setExternalPayment: (externalPayment: ExternalPayment | undefined) =>
    set({ externalPayment }),
  setYd: (yd) => set({ yd }),
  setWalletCapabilities: (walletCapabilities) => set({ walletCapabilities }),
  setTheme: (theme: YodlTheme) => set({ theme }),
  setUrlConfig: (urlConfig) => set({ urlConfig }),
  setHandle: (handle) => set({ handle }),
  setPlaywright: (playwrightAddress) => set({ playwright: playwrightAddress }),
  setReceiver: (receiver) => set({ receiver }),
  setSender: (sender) => set({ sender }),

  setTokenSelection: (selectedTokenIn) =>
    set({ tokenSelection: selectedTokenIn }),

  setTokenOut: (tokenOut) => set({ tokenOut }),
  setTokenIn: (tokenIn) => set({ tokenIn }),

  setTokenInAmount: (tokenInAmount) => set({ tokenInAmount }),
  setTokenOutAmount: (tokenOutAmount) => set({ tokenOutAmount }),
  // Save invoice amount as integer
  setInvoiceAmount: (invoiceAmount) => set({ invoiceAmount }),

  setInvoiceCurrency: (invoiceCurrency) => set({ invoiceCurrency }),
  setPricefeeds: (pricefeeds: YodlPaymentPriceFeedTuple | undefined) =>
    set({ pricefeeds }),
  setExchangeRate: (exchangeRate: bigint | undefined) => set({ exchangeRate }),

  setSwapQuote: (swapQuote: Quote | undefined) => set({ swapQuote }),

  setPreferencesFromYodl: (preferencesFromYodl: PaymentConfig | undefined) =>
    set({ preferencesFromYodl }),
  setPreferencesFromENS: (preferencesFromENS: PaymentConfig | undefined) =>
    set({ preferencesFromENS }),

  setSavedLinks: (savedLinks: SavedLink[]) => set({ savedLinks }),
  setRecentTransactionHash: (recentTransactionHash) =>
    set({ recentTransactionHash }),

  setHideUserPopups: (hideUserPopups) => set({ hideUserPopups }),

  setSelectedPaymentPreference: (
    selectedPaymentPreference: PaymentPreferences,
  ) => set({ selectedPaymentPreference }),

  setPaymentNotification: (paymentNotification: any) =>
    set({ paymentNotification }),

  setAcrossQuote: (acrossQuote: AcrossQuote | undefined) =>
    set({ acrossQuote }),

  setPaymentMemo: (paymentMemo) => set({ paymentMemo }),

  setCommunityInfo: (communityInfo) => set({ communityInfo }),

  setRedirectUrl: (redirectUrl: string | undefined) => {
    // Enable auto-redirect if the user was redirected from the same URL they're being redirected back to
    if (typeof window !== 'undefined' && redirectUrl) {
      const referrer = document.referrer;
      if (referrer) {
        try {
          const referrerUrl = new URL(referrer);
          const redirectToUrl = new URL(redirectUrl);

          // Compare the hostname and pathname
          if (referrerUrl.hostname === redirectToUrl.hostname) {
            console.log('Auto-redirecting to', redirectUrl);
            set({ redirectUrl, autoRedirect: true });
            return;
          }
        } catch (e) {
          // Invalid URL format, fall through to default behavior
        }
      }
    }
    set({ redirectUrl });
  },

  setAutoRedirect: (autoRedirect: boolean) => set({ autoRedirect }),

  setPmtMetaCommunity: (pmtMetaCommunity: Address | undefined) =>
    set({ pmtMetaCommunity }),
  setPmtMetaYapp: (pmtMetaYapp: [Address, string] | undefined) =>
    set({ pmtMetaYapp }),

  reset: () =>
    set((state) => {
      const { urlConfig } = state;
      return { ...initialState, urlConfig };
    }),
}));

export const paymentStore = useYodlStore;
